export const SCALE = [
  { value: 1, label: "Never true" },
  { value: 2, label: "Rarely true" },
  { value: 3, label: "Sometimes true" },
  { value: 4, label: "Often true" },
  { value: 5, label: "Always true" },
];

export const AREAS = [
  {
    key: "identity",
    area: "Identity",
    guidingQuestion: "Can I clearly name who I am apart from what I do for others?",
    questions: [
      "I feel grounded in who I am even when my roles or titles change.",
      "I easily separate my personal self-worth from my productivity and achievements.",
      "I can articulate my personal vision without referencing my family or job obligations.",
      "I take time to nurture my personal interests and passions without feeling guilty.",
    ],
    affirmation:
      "You know who you are beneath every title you carry. Your sense of self is not up for negotiation — it is the ground you lead from.",
    gapExplanation:
      "When Identity is your growth area, your sense of self has quietly fused with your roles. You may be highly capable and deeply needed — yet unsure who you are when no one needs anything from you.",
    intentionalAction:
      "This week, write three sentences that begin with \u201cI am\u201d — none of them may mention a role, a job, or a relationship.",
  },
  {
    key: "decision_making",
    area: "Decision-Making",
    guidingQuestion:
      "Are my decisions intentional, or mostly driven by obligation, guilt, expectations, or urgency?",
    questions: [
      "I make major life choices based on my core values rather than external pressure.",
      "I comfortably say 'no' to opportunities that do not align with my top priorities.",
      "I pause and evaluate decisions rather than reacting immediately to urgency.",
      "I trust my inner wisdom when making difficult choices.",
    ],
    affirmation:
      "Your decisions are your own. You choose from values, not from pressure — and that intentionality shapes everything you build.",
    gapExplanation:
      "When Decision-Making is your growth area, obligation, guilt, and urgency are quietly steering choices that belong to you. You are leading your life by reaction instead of intention.",
    intentionalAction:
      "Before your next yes, pause for 24 hours and ask one question: is this values-led, or guilt-led?",
  },
  {
    key: "boundaries",
    area: "Boundaries",
    guidingQuestion:
      "Do my boundaries reflect what I value, or do I constantly adjust myself for others?",
    questions: [
      "I clearly communicate my limits to others before reaching burn-out.",
      "I protect my personal time and energy even when others request my help.",
      "I avoid over-explaining or apologizing when setting reasonable boundaries.",
      "I hold space for my own peace without accommodating everyone else's emotions.",
    ],
    affirmation:
      "Your boundaries are sacred architecture. You protect what matters without apology, and others rise to meet the standard you set.",
    gapExplanation:
      "When Boundaries is your growth area, you are likely adjusting yourself to keep the peace — absorbing what was never yours to carry. The cost is quiet, compounding exhaustion.",
    intentionalAction:
      "Choose one relationship or commitment and state one clear limit this week — kindly, and without over-explaining.",
  },
  {
    key: "capacity",
    area: "Capacity",
    guidingQuestion:
      "Am I intentionally managing what I carry, or automatically taking on more because I know I can?",
    questions: [
      "I regularly assess my emotional and physical load before making new commitments.",
      "I delegate tasks freely instead of feeling I must do everything myself.",
      "I build intentional white space and rest into my weekly schedule.",
      "I recognize when I am near capacity and actively step back.",
    ],
    affirmation:
      "You steward your energy with wisdom. You carry what is yours — and you have learned the strength of setting the rest down.",
    gapExplanation:
      "When Capacity is your growth area, your competence has become a trap: because you can carry more, you do. Strength without stewardship eventually becomes depletion.",
    intentionalAction:
      "Remove or delegate one recurring responsibility this week — and fiercely protect the white space it creates.",
  },
  {
    key: "alignment",
    area: "Alignment",
    guidingQuestion:
      "Does the way I currently live reflect who I say I am and what matters to me?",
    questions: [
      "My daily calendar reflects the things I claim are most important to me.",
      "I feel a deep sense of harmony between my inner convictions and outer actions.",
      "I regularly audit my life to ensure my habits support my higher purpose.",
      "I live courageously in my truth rather than conforming to expectations.",
    ],
    affirmation:
      "Your outer life is beginning to tell the truth about your inner one. That congruence is rare — and it is powerful.",
    gapExplanation:
      "When Alignment is your growth area, there is a gap between the woman you say you are and the life you are actually living. Closing it starts with honesty, not effort.",
    intentionalAction:
      "Today, hold one hour of your calendar up against your stated values — and adjust one thing that does not match.",
  },
];

export const ALL_QUESTIONS = AREAS.flatMap((a) =>
  a.questions.map((q, i) => ({ areaKey: a.key, area: a.area, text: q, index: i }))
);

export function computeResults(answers) {
  const scores = {};
  AREAS.forEach((a) => {
    const total = ALL_QUESTIONS.reduce(
      (sum, q, i) => (q.areaKey === a.key ? sum + answers[i] : sum),
      0
    );
    scores[a.area] = Math.round((total / (a.questions.length * 5)) * 100);
  });
  const entries = Object.entries(scores);
  const strongest = entries.reduce((a, b) => (b[1] >= a[1] ? b : a))[0];
  const gap = entries.reduce((a, b) => (b[1] <= a[1] ? b : a))[0];
  const overall = Math.round(entries.reduce((s, [, v]) => s + v, 0) / entries.length);
  return { scores, strongest, gap, overall };
}

export const areaByName = (name) => AREAS.find((a) => a.area === name);
